import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import Card from '../components/common/Card';
import StatusBadge from '../components/common/StatusBadge';
import { SkeletonCard, SkeletonTable } from '../components/common/Skeleton';
import { TrendingUp, DollarSign, Clock, PieChart, AlertCircle } from 'lucide-react';

const ProfitSharingPage = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ totalUserPnL: 0, totalProfitShared: 0, totalUserNet: 0 });
  const [loading, setLoading] = useState(true);

  const fetchRecords = async () => {
    try {
      const res = await axiosClient.get('/profit/my-records');
      setRecords(res.data.records || []);
      setSummary(res.data.summary || { totalUserPnL: 0, totalProfitShared: 0, totalUserNet: 0 });
    } catch (err) {
      console.error('[Profit Records Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <h2 className="text-lg font-extrabold text-[#FFFFFF]">Periodic PnL Synchronization & Profit Sharing</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Trading PnL is synchronized automatically at regular intervals. Deductions and profit share distributions are calculated seamlessly.
          </p>
        </div>
        <div className="flex items-center space-x-2 bg-[#09090B] px-3.5 py-2 rounded-xl border border-[#27272A] text-xs font-semibold text-[#F59E0B]">
          <Clock className="w-4 h-4" />
          <span>Automated PnL Synchronization</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Total Closed PnL</span>
                <TrendingUp className="w-4 h-4 text-[#10B981] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#10B981] mt-2">+${summary.totalUserPnL.toFixed(2)}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Cumulative closed trading profit</p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Profit Share Paid</span>
                <DollarSign className="w-4 h-4 text-[#F97316] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#F97316] mt-2">${summary.totalProfitShared.toFixed(2)}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1">Deducted from internal wallet</p>
            </Card>

            <Card hoverEffect>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#9CA3AF] font-semibold">Your Net Remaining Profit</span>
                <PieChart className="w-4 h-4 text-[#FFFFFF] shrink-0" />
              </div>
              <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">${summary.totalUserNet.toFixed(2)}</p>
              <p className="text-[10px] text-[#10B981] mt-1">Net profit after sharing</p>
            </Card>
          </>
        )}
      </div>

      {/* Records Table */}
      <Card title="Synchronized MT5 PnL & Profit Deduction Logs" subtitle="Periodic closed trading results and applicable profit sharing deductions">
        {loading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : records.length === 0 ? (
          <div className="p-8 text-center space-y-3">
            <TrendingUp className="w-10 h-10 text-[#9CA3AF]/40 mx-auto" />
            <p className="text-xs text-[#9CA3AF]">No PnL synchronization records recorded yet.</p>
            <p className="text-[10px] text-[#9CA3AF]">Records will automatically appear after the next periodic sync cycle.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">MT5 Account</th>
                  <th className="py-3 px-4">Closed PnL</th>
                  <th className="py-3 px-4">Rate (%)</th>
                  <th className="py-3 px-4">Profit Share</th>
                  <th className="py-3 px-4">Net User Profit</th>
                  <th className="py-3 px-4">Deduction Status</th>
                  <th className="py-3 px-4">Synced Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {records.map((rec) => (
                  <tr key={rec._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-[#FFFFFF]">
                      #{rec.mt5AccountId?.accountNumber || 'MT5'}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#10B981]">
                      +${rec.closedPnL.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-[#F59E0B]">
                      {rec.profitShareRate}%
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#F97316]">
                      -${rec.profitShareAmount.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#FFFFFF]">
                      +${rec.userNetProfit.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge 
                        status={rec.deductedFromWallet ? 'approved' : 'pending'} 
                        label={rec.deductedFromWallet ? 'Deducted' : 'Pending Wallet Bal'} 
                      />
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(rec.syncedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ProfitSharingPage;
